import { useCallback, useEffect, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

function ActionBadge({ action }) {
  const map = {
    bulk_delete_missions: "bg-indigo-50 text-indigo-700 ring-indigo-100",
    bulk_delete_orgs: "bg-amber-50 text-amber-700 ring-amber-100",
    bulk_delete_users: "bg-rose-50 text-rose-700 ring-rose-100",
  };
  const cls = map[action] || "bg-slate-100 text-slate-700 ring-slate-200";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${cls}`}>{action.replace(/_/g, " ")}</span>
  );
}

function SummaryPre({ summary }) {
  return (
    <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-700 ring-1 ring-slate-200">
      {JSON.stringify(summary, null, 2)}
    </pre>
  );
}

export default function AdminAudit() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/deletion-audits", { params: { limit: 100 } });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-audit-page">
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_audit.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-testid="admin-audit-title">
            {t("admin_audit.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t("admin_audit.subtitle")}</p>
        </div>
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {loading ? (
            <p className="text-sm text-slate-500 p-2">{t("common.loading")}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 p-2" data-testid="admin-audit-empty">{t("admin_audit.empty")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 text-left">{t("admin_audit.col_date")}</th>
                  <th className="py-2 text-left">{t("admin_audit.col_admin")}</th>
                  <th className="py-2 text-left">{t("admin_audit.col_action")}</th>
                  <th className="py-2 text-left">{t("admin_audit.col_targets")}</th>
                  <th className="py-2 text-right">{t("admin_audit.col_details")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = !!expanded[r.id];
                  return (
                    <Fragment key={r.id}>
                      <tr className="border-t border-slate-100" data-testid={`audit-row-${r.id}`}>
                        <td className="py-2 text-slate-500">{formatDateShort(r.created_at)}</td>
                        <td className="py-2 text-slate-700">{r.admin_email}</td>
                        <td className="py-2"><ActionBadge action={r.action} /></td>
                        <td className="py-2 text-slate-700">{r.target_ids?.length ?? 0}</td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}
                            className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            data-testid={`audit-toggle-${r.id}`}
                          >
                            {isOpen ? t("admin_audit.hide") : t("admin_audit.show")}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-slate-100 bg-slate-50/40" data-testid={`audit-detail-${r.id}`}>
                          <td colSpan={5} className="px-2 py-3">
                            <SummaryPre summary={r.summary || {}} />
                            {r.target_ids?.length > 0 && (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-xs font-semibold text-slate-600">
                                  {t("admin_audit.target_ids")} ({r.target_ids.length})
                                </summary>
                                <p className="mt-1 break-all font-mono text-[11px] text-slate-500">{r.target_ids.join(", ")}</p>
                              </details>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}
